/**
 * Pre-export determinism snapshot + post-export parity assertions.
 *
 * captureExportState(canvasEl, requestedW, requestedH):
 *   Returns and logs a deterministic state record covering every
 *   environment variable that can shift export output run-to-run —
 *   DPR, viewport, scroll, zoom transform, canvas bbox, font readiness.
 *   Logged under `[export-state]` so future regressions can be replayed
 *   from the same starting conditions.
 *
 * assertExportParity({ snapshot, exportedCanvas, clipFailures,
 *                      textFailures, wholeDiff, perElementDiff }):
 *   Validates the export against parity invariants:
 *     - exported canvas pixel dimensions == requestedExport * 2 (scale)
 *     - no unresolved clip-path flatten failures
 *     - no text-serialization failures
 *     - whole-canvas diffRatio under threshold
 *     - no per-element diffRatio exceeding the per-element threshold
 *     - every <img> in the cloned canvas decoded
 *   If `window.__exportStrict === true`, fails by throwing.
 *   Otherwise logs `[export-parity-fail]` with the violations list.
 */

import { computeStateHash } from './exportStabilize';

export function captureExportState(canvasEl, requestedW, requestedH) {
  if (typeof window === 'undefined' || !canvasEl) return null;
  const bbox = canvasEl.getBoundingClientRect();
  const snap = {
    timestamp: Date.now(),
    dpr: window.devicePixelRatio || 1,
    viewport: { w: window.innerWidth, h: window.innerHeight },
    scroll:   { x: window.scrollX,    y: window.scrollY },
    canvas: {
      id: canvasEl.id || null,
      styleSize:  { w: canvasEl.style.width,  h: canvasEl.style.height },
      offsetSize: { w: canvasEl.offsetWidth,  h: canvasEl.offsetHeight },
      bbox: { x: bbox.x, y: bbox.y, w: bbox.width, h: bbox.height },
      transform: canvasEl.style.transform || 'none',
      computedTransform: window.getComputedStyle(canvasEl).transform,
    },
    requestedExport: { w: requestedW, h: requestedH },
    fonts: {
      status: document?.fonts?.status ?? 'unknown',
      loadedCount: document?.fonts ? document.fonts.size : 0,
    },
    // Cache-invalidation key — any change to font set / zoom / DPR /
    // viewport / canvas size shifts this hash, so a stale snapshot
    // taken before the change can be detected and discarded.
    stateHash: computeStateHash(canvasEl),
  };
  if (window.__exportDiagnostics) console.log('[export-state]', snap);
  return snap;
}

/**
 * Verify the captured snapshot still describes the current rendering
 * environment. Returns { stale: boolean, current: hash, captured: hash }.
 * Called right before reading the export pixel buffer — if `stale`, the
 * snapshot's measurements must not be reused for parity assertions.
 */
export function verifySnapshotFresh(canvasEl, snapshot) {
  if (!snapshot) return { stale: true, current: null, captured: null };
  const current = computeStateHash(canvasEl);
  return { stale: current !== snapshot.stateHash, current, captured: snapshot.stateHash };
}

const DEFAULT_THRESHOLDS = {
  wholeDiffRatio:     0.05,
  perElementRatio:    0.10,
  textElementRatio:   0.02,
};

export function assertExportParity({
  snapshot,
  exportedCanvas,
  clipFailures = [],
  textFailures = [],
  wholeDiff,
  perElementDiff,
  clonedCanvasEl,
  thresholds = {},
}) {
  if (typeof window === 'undefined') return [];
  const T = { ...DEFAULT_THRESHOLDS, ...thresholds };
  const violations = [];

  // (1) Pixel-dimension equivalence.
  if (snapshot && exportedCanvas) {
    const expectedW = snapshot.requestedExport.w * 2;
    const expectedH = snapshot.requestedExport.h * 2;
    if (exportedCanvas.width !== expectedW || exportedCanvas.height !== expectedH) {
      violations.push({
        kind: 'canvas-size-mismatch',
        expected: { w: expectedW, h: expectedH },
        actual:   { w: exportedCanvas.width, h: exportedCanvas.height },
      });
    }
  }

  // (2) Clip-path flatten failures — every URL ref must have been converted.
  if (clipFailures.length > 0) {
    violations.push({ kind: 'unresolved-clip-flatten', failures: clipFailures });
  }

  // (3) Text serialization failures.
  if (textFailures.length > 0) {
    violations.push({ kind: 'text-serialize-failed', failures: textFailures });
  }

  // (4) Whole-canvas diff threshold.
  if (wholeDiff && wholeDiff.diffRatio > T.wholeDiffRatio) {
    violations.push({
      kind: 'whole-diff-exceeded',
      diffRatio: wholeDiff.diffRatio,
      threshold: T.wholeDiffRatio,
      bbox: wholeDiff.mismatchBBox,
    });
  }

  // (5) Per-element diff — text elements held to a tighter threshold,
  //     since SVG text serialization should give renderer-equivalent output.
  if (Array.isArray(perElementDiff)) {
    for (const e of perElementDiff) {
      const limit = e.type === 'text' ? T.textElementRatio : T.perElementRatio;
      if (e.diffRatio > limit) {
        violations.push({
          kind: 'element-diff-exceeded',
          id: e.id,
          type: e.type,
          diffRatio: e.diffRatio,
          threshold: limit,
          bbox: e.bbox,
        });
      }
    }
  }

  // (6) Missing image decode — any <img> that failed to load in the cloned canvas.
  //     Note: we walk the LIVE canvas here, since the clone is gone by now;
  //     in practice html2canvas would have failed loudly if a clone img couldn't
  //     decode, but checking the live tree catches broken sources pre-export.
  if (clonedCanvasEl && clonedCanvasEl.querySelectorAll) {
    const imgs = clonedCanvasEl.querySelectorAll('img');
    const broken = [];
    imgs.forEach((img) => {
      if (!img.complete || img.naturalWidth === 0) {
        broken.push(img.getAttribute('src') || '(no src)');
      }
    });
    if (broken.length > 0) {
      violations.push({ kind: 'image-decode-failed', srcs: broken });
    }
  }

  if (violations.length === 0) return violations;

  const msg = `Export parity check failed (${violations.length} violation${violations.length === 1 ? '' : 's'})`;
  if (window.__exportStrict) {
    const err = new Error(msg);
    err.violations = violations;
    console.error('[export-parity-fail]', violations);
    throw err;
  }
  console.error('[export-parity-fail]', violations);
  return violations;
}
